import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import React, { ReactNode, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectMindItem, selectSiblings } from '../../../reducers/mindItems.selectors';
import { selectProduct } from '../../../reducers/products.selectors';
import { StoreType } from '../../../reducers';
import { publisherImages } from '../../../assets/publisher/svg';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, SxProps, Theme, Typography
} from '@mui/material';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import getTextTruncationSx from '../../../utils/getTextTruncationSx';
import { NewAttachmentsButton } from '../../AttachmentsButton';
import { Product } from '@klab-berlin/api-sdk/lib/types/responses/Product';
import { getDateInGermanFormat } from '../../../utils/format';
import { Metadata } from '../../cards/MuiCard/MuiCardBase/MuiCardBase';
import useRouter from '../../../utils/useRouter';
import { compressClassYearArray } from '../../../utils/compressClassYearArray';
import WithTruncationAnimation from '../../utils/WithTruncationAnimation';
import { ReaderContext } from '../../Reader/ReaderContext';
import { noop } from 'lodash';
import { Hint } from '../../Hint';
import useScreenSize from '../../../utils/useScreenSize';
import Tooltip from '../../Tooltip';

interface ReaderDetailsProps {
  topRightAction?: {
    icon: ReactNode;
    onClick: () => void;
    buttonSx?: SxProps<Theme>;
  };
  isContentHidden?: boolean;
  mindItemId: string;
  allowOnboarding?: boolean;
}

const ReaderDetails : React.FC<ReaderDetailsProps> = ({
  topRightAction,
  isContentHidden,
  mindItemId,
  allowOnboarding,
}) => {
  const { t } = useTranslation();

  const mindItem = useSelector((state: StoreType) => selectMindItem(state, mindItemId));
  const siblingMindItems =
    useSelector(state => mindItem ? selectSiblings(state, { id: mindItem.id, type: 'mindItem' }) : []);
  const product =
    useSelector((state: StoreType) => mindItem ? selectProduct(state, mindItem.product) : undefined);

  if (!product || !mindItem) return null;

  const productPublisher = (product.publisher || mindItem.publisher) as keyof typeof publisherImages;

  return <Box sx={{
    display: isContentHidden ? 'none' : 'block',
    overflow: 'hidden',
    width: customSizes.readerDetails.width,
  }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: customSizes.readerDetails.headerHeight,
      paddingX: spaces.md,
    }}>
      <Typography variant='overline' textTransform='uppercase'>
        {t('Product details')}
      </Typography>
      {
        topRightAction && <IconButton onClick={topRightAction.onClick} sx={{
          color: palette.text.primary,
          ...topRightAction.buttonSx,
        }}>
          {topRightAction.icon}
        </IconButton>
      }
    </Box>
    <Box sx={{
      overflowY: 'scroll',
      height: `calc(100% - ${customSizes.readerDetails.headerHeight})`,
    }}>
      <Box
        sx={{
          padding: spaces.md,
          paddingTop: 0,
          gap: spaces.xxs,
        }}
      >
        <Box
          data-testid='product-details'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.lg,
          }}
        >
          <Box sx={{
            display: 'flex',
            gap: spaces.md,
            flexDirection: 'column',
          }}>
            {
              publisherImages[productPublisher] && <Box>
                <Box sx={{
                  padding: spaces.xs,
                  height: customSizes.readerDetails.publisherLogoHeight,
                  width: customSizes.readerDetails.publisherLogoWidth,
                  borderRadius: spaces.sm,
                  border: `1px solid ${palette.elevation.outlined}`,
                }}
                component={publisherImages[productPublisher]}
                />
              </Box>
            }
            {
              product.title && <Typography
                variant='h6'
                sx={{
                  ...getTextTruncationSx(2),
                }}
              >
                {product.title}
              </Typography>
            }
            <MetadataReader product={product}/>
          </Box>
          {product.attachments.length > 0 && <NewAttachmentsButton
            productId={mindItem.product}
            attachmentsCount={product.attachments.length}
            size='small'
          />}
        </Box>
      </Box>
      <Box
        sx={{
          padding: spaces.md,
          paddingBottom: spaces.sm,
        }}
        data-testid='product-details__number-of-mind-items'
      >
        <Typography variant='overline'>
          {`${product.mindItems.length} ${product.mindItems.length === 1 ? t('Unit') : t('Units')}`}
        </Typography>
      </Box>
      <AccordionReader mindItems={siblingMindItems} currentMindItemId={mindItem.id} allowOnboarding={allowOnboarding}/>
    </Box>
  </Box>;
};

const MetadataReader: React.FC<{product: Product}> = ({ product }) => {
  const { t } = useTranslation();
  const date = product.publisherReleaseDate && getDateInGermanFormat(product.publisherReleaseDate);
  const metadataStyles = {
    flex: '1 0 0',
  };
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: spaces.md,
  }}>
    {
      (product.subjects.length > 0 || product.classYears.length > 0) &&
      <MetadataReaderRow>
        <Tooltip label={product.subjects.join(', ')} placement='top-start'>
          <Metadata
            sx={metadataStyles}
            icon='tag'
            label={product.subjects.join(', ')}
          />
        </Tooltip>
        <Tooltip label={compressClassYearArray(product.classYears).join(', ')} placement='top-start'>
          <Metadata 
            sx={metadataStyles}
            icon='teacher'
            label={compressClassYearArray(product.classYears).join(', ') + ' ' + t('Class year')}
          />
        </Tooltip>
      </MetadataReaderRow>
    }
    {
      (product.author || date) &&
      <MetadataReaderRow>
        {
          product.author && 
          <Tooltip label={product.author} placement='top-start'>
            <Metadata
              sx={metadataStyles}
              icon='feather'
              label={product.author}
            />
          </Tooltip>
        }
        {
          date && 
          <Tooltip label={date} placement='top-start'>
            <Metadata
              sx={metadataStyles}
              icon='calendar'
              label={date}
            />
          </Tooltip>
        }
      </MetadataReaderRow>
    }
  </Box>;
};

const AccordionReader: React.FC<{
  mindItems: MindItem[],
  currentMindItemId: string,
  allowOnboarding?: boolean,
}> = ({ mindItems, currentMindItemId, allowOnboarding }) => {
  const { router } = useRouter();
  const { isLargeScreen } = useScreenSize();
  const { t } = useTranslation();
  const { onboardingStepCounter } = useContext(ReaderContext);
  const refs = React.useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const button = refs.current[currentMindItemId];
    if (currentMindItemId && button) {
      button.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentMindItemId]);

  return <Box sx={{ paddingX: spaces.md }} data-testid='accordion-reader'>
    {mindItems.map((mindItem, index) => {
      const isCurrentMindItem = mindItem.id === currentMindItemId;

      const getToggleButton = (isTruncated: boolean) => <Button
        variant='text'
        size='small'
        color='secondary'
      >
        {isTruncated ? t('Show more') : t('Show less')}
      </Button>;

      let component = <Accordion
        key={mindItem.id}
        expanded={isCurrentMindItem}
        onChange={() => {
          router.push(`/dokument/${mindItem.id}`);
        }}
        sx={{
          margin: '0 !important',
          '::before': {
            display: 'none',
          },
        }}
        elevation={0}
      >
        <AccordionSummary 
          sx={{
            padding: 0,
            margin: 0,
            '& .MuiAccordionSummary-content': {
              margin: '0 !important',
            },
            '&.Mui-expanded': {
              minHeight: 'unset',
            },
          }}
        >
          <Button
            variant='text'
            size='large'
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              textAlign: 'left',
              ...(isCurrentMindItem ? {
                backgroundColor: palette.primary.hover
              } : {}),
            }}
            ref={el => refs.current[mindItem.id] = el}
          >
            {`${index + 1}. ${mindItem.title}`}
          </Button>
        </AccordionSummary>
        <AccordionDetails sx={{
          paddingY: spaces.xs,
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xs,
          paddingLeft: spaces.xxs,
          alignItems: 'flex-start',
        }}>
          <WithTruncationAnimation
            maxLines={7}
            renderToggleButton={getToggleButton}
          >
            <Typography
              variant='body2'
              sx={{
                paddingX: spaces.xs,
              }}
            >
              {mindItem.description}
            </Typography>
          </WithTruncationAnimation>
        </AccordionDetails>
      </Accordion>;

      if (allowOnboarding && index === 0 && isLargeScreen) {
        component = <Hint
          width={334}
          open={onboardingStepCounter?.step === 1}
          onClose={onboardingStepCounter ? onboardingStepCounter.dismiss : noop}
          placement='right'
          description={t('Use the overview to quickly switch between units.')}
          key={index}
          actions={{
            primary: {
              label: t('Continue'),
              onClick: onboardingStepCounter ? onboardingStepCounter.incrementStep : noop,
              buttonProps: {
                variant: 'text',
                size: 'small',
                sx: {
                  color: palette.primary.contrastText,
                }
              }
            },
          }}
          bottomLeftText='1/3'
        >
          {component}
        </Hint>;
      }
      
      return component;
    })}
  </Box>;
};

const MetadataReaderRow: React.FC = (props) => {
  return <Box sx={{
    display: 'flex',
    gap: spaces.md,
  }}>
    {
      Array.isArray(props.children) && 
      props.children.map((child, index) => <Box key={index} sx={{ flex: 1 }}>{child}</Box>)
    }
  </Box>;
};

export default ReaderDetails;
